/**
 * Cloud upload — batch and realtime upload functions.
 *
 * Handles payload construction, gzip compression for large payloads,
 * retry with exponential backoff, rate limit handling, and structured
 * failure reporting.
 *
 * Constitution Principle VIII: HTTPS enforced via cloud-auth.ts.
 * Never throws to caller — returns structured results.
 */

import { gzip } from 'node:zlib';
import { promisify } from 'node:util';
import type {
  TestRunReport,
  GitMetadata,
  CIMetadata,
  Summary,
  TimelineStep,
} from '@testrelic/core';
import type { SdkRunType } from './run-type-env.js';
import { resolveSdkRunTypeFromEnv } from './run-type-env.js';
import { Budget, withTimeout } from './cloud-deadline.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TestResultForUpload {
  readonly testId: string;
  readonly title: string;
  readonly status: string;
  readonly duration: number;
  readonly suiteName: string;
  readonly filePath: string;
  readonly testType: string;
  readonly isFlaky: boolean;
  readonly startedAt: string;
  readonly completedAt: string;
  readonly tags: readonly string[];
  readonly failure: unknown | null;
  /** Current attempt index (0 = first run, 1 = first retry, etc.) */
  readonly retry: number;
  /** Total number of retries that occurred (test.results.length - 1) */
  readonly retryCount: number;
  /** Human-readable retry outcome, e.g. "passed on retry 1", or null if no retries */
  readonly retryStatus: string | null;
  /** Testing platform/origin, e.g. "Local", "GitHub Actions", "BrowserStack" */
  readonly source?: string;
  /** UI tests — browser name, e.g. "Chrome", "Firefox", "WebKit" */
  readonly browser?: string;
  /** UI tests — operating system, e.g. "Windows", "macOS", "Linux" */
  readonly os?: string;
  /** API tests — test runner, e.g. "Newman Runner", "Playwright API Client" */
  readonly runner?: string;
  /** API tests — protocol, e.g. "rest", "graphql", "grpc". Drives API-workspace routing. */
  readonly apiProtocol?: string;
  /** UI tests — viewport resolution, e.g. "1920x1080" */
  readonly resolution?: string;
  /**
   * Playwright action steps (click, fill, expect, etc.) included in the batch
   * payload as a reliable fallback for the fire-and-forget per-test realtime
   * upload. If the realtime call succeeds first, the server deduplicates by
   * skipping reingest. If it fails silently, the batch guarantees delivery.
   */
  readonly actions?: readonly Record<string, unknown>[];
  /** Console log entries captured during this test — batch fallback. */
  readonly consoleLogs?: readonly Record<string, unknown>[];
  /** Network/HAR request entries captured during this test — batch fallback. */
  readonly networkRequests?: readonly Record<string, unknown>[];
  /**
   * Navigation entries captured during this test — batch fallback. This is the
   * fix for Test Navigation never reaching the server under the default 'batch'
   * upload strategy: the realtime per-test path (which carries navigations) never
   * runs, and the streaming report's timeline is empty, so the batch per-test
   * payload is the only delivery route. Small (a few URL entries), so the payload
   * size guard above never strips it.
   */
  readonly navigations?: readonly Record<string, unknown>[];
  /**
   * API request/response calls in the canonical `apiReport.apiCalls` shape —
   * batch fallback so the API-workspace inspector renders req/res even when the
   * fire-and-forget realtime per-test upload is lost. Without this the batch
   * re-ingest would overwrite the realtime apiReport with one that has no calls.
   */
  readonly apiCalls?: readonly Record<string, unknown>[];
  /** Captured API assertions in the canonical `protocolAssertions` shape — batch fallback. */
  readonly protocolAssertions?: readonly Record<string, unknown>[];
}

export interface RunUploadPayload {
  readonly runId: string;
  readonly repoGitId: string;
  readonly startedAt: string;
  readonly summary: Summary;
  readonly timeline: readonly TimelineStep[];
  readonly tests?: readonly TestResultForUpload[];
  readonly branch?: string;
  readonly commit?: string;
  readonly commitMessage?: string;
  readonly commitAuthor?: string;
  readonly finishedAt?: string;
  readonly duration?: number;
  readonly ciProvider?: string;
  readonly ciRunUrl?: string;
  /** Where tests ran: "local", "github-actions", "gitlab-ci", etc. */
  readonly environment?: string;
  /** Cloud-farm platform this run executed on (BrowserStack / LambdaTest). */
  readonly cloudPlatform?: 'browserstack' | 'lambdatest';
  readonly cloudBuildId?: string;
  readonly cloudSessionId?: string;
  /** When set, monitoring dashboards (/monitoring/*) include this run for that bucket. */
  readonly runType?: SdkRunType;
  /** Run-level API signal — "api" when every test in the run is an API test. Lets the cloud open the API workspace without a ?testType=api URL param. */
  readonly testType?: string;
  /** Dominant API protocol for the run (e.g. "rest"). Pairs with testType==='api'. */
  readonly apiProtocol?: string;
  /** Free-form run metadata; mirrors the cloud contract (`testType`/`primaryProtocol`) so already-deployed servers classify the run too. */
  readonly metadata?: Record<string, unknown>;
}

export interface UploadResult {
  readonly success: boolean;
  readonly statusCode: number | null;
  readonly error: string | null;
}

export interface UploadFailure {
  readonly success: false;
  readonly reason: string;
  readonly statusCode: number | null;
  readonly payload: Record<string, unknown>;
  readonly targetEndpoint: string;
  readonly method: string;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const GZIP_THRESHOLD_BYTES = 1_048_576; // 1MB
const RETRY_DELAYS_MS = [1000, 3000, 9000];
const MAX_RETRIES = 3;
/** Per-request ceiling for the JSON calls (run upload, init, finalize). The
 *  config's own `timeout` overrides it — see cloud-deadline for why bare
 *  `fetch` could not be left to undici's 5-minute defaults. */
const DEFAULT_REQUEST_TIMEOUT_MS = 30_000;
/** Strip per-test consoleLogs/networkRequests from the batch if the total
 *  serialized payload exceeds this ceiling. The batch request is gzipped before
 *  upload (~10x smaller) and the server accepts 100MB *decompressed*, so this
 *  sits just under that limit — detail is kept for all realistic runs and the
 *  strip is a last resort (with a warning), never a silent drop. The default
 *  'batch' strategy never runs the realtime per-test path, so the batch arrays
 *  are the ONLY copies the cloud receives. */
const MAX_BATCH_PAYLOAD_BYTES = 90 * 1_048_576; // 90MB raw (server inflates to <=100MB)

// ---------------------------------------------------------------------------
// Payload Construction
// ---------------------------------------------------------------------------

export interface RunApiSignal {
  readonly testType: 'api';
  readonly apiProtocol: string;
  readonly metadata: { readonly testType: 'api'; readonly primaryProtocol: string };
}

export interface RunTestTypeSignal {
  /** Run-level test type the cloud labels: Web / API / Mobile / Unit. */
  readonly testType: 'e2e' | 'api' | 'mobile' | 'unit';
  /** Dominant API protocol — present only when testType === 'api'. */
  readonly apiProtocol?: string;
  /** Mirrors the cloud contract — present only when testType === 'api'. */
  readonly metadata?: { readonly testType: 'api'; readonly primaryProtocol: string };
}

/** Non-api per-test slugs eligible for the run-level most-common fallback. */
const RUN_NONAPI_SLUGS = new Set(['e2e', 'mobile', 'unit']);

/**
 * Run-level test-type classification for a Playwright run.
 *
 *   1. EVERY test is an API test ⇒ 'api' (+ dominant protocol). Requiring *all*
 *      tests avoids misrouting a mixed UI+API run to the API report, and keeps
 *      the API-workspace routing (which keys on testType === 'api') intact.
 *   2. else the most-common recognized non-api slug ({e2e, mobile, unit}).
 *   3. else 'e2e' — Playwright is browser-driven, so Web is the capture-truth
 *      default for an otherwise-unclassified run.
 */
export function deriveRunTestType(
  tests: readonly TestResultForUpload[] | undefined,
): RunTestTypeSignal {
  if (tests && tests.length > 0 && tests.every((t) => t.testType === 'api')) {
    const counts = new Map<string, number>();
    for (const t of tests) {
      const proto = t.apiProtocol ?? 'rest';
      counts.set(proto, (counts.get(proto) ?? 0) + 1);
    }
    let primaryProtocol = 'rest';
    let max = 0;
    for (const [proto, n] of counts) {
      if (n > max) { max = n; primaryProtocol = proto; }
    }
    return { testType: 'api', apiProtocol: primaryProtocol, metadata: { testType: 'api', primaryProtocol } };
  }

  const counts = new Map<string, number>();
  for (const t of tests ?? []) {
    if (RUN_NONAPI_SLUGS.has(t.testType)) counts.set(t.testType, (counts.get(t.testType) ?? 0) + 1);
  }
  let mostCommon: 'e2e' | 'mobile' | 'unit' | null = null;
  let max = 0;
  for (const [slug, n] of counts) {
    if (n > max) { max = n; mostCommon = slug as 'e2e' | 'mobile' | 'unit'; }
  }
  return { testType: mostCommon ?? 'e2e' };
}

/**
 * Back-compat wrapper preserving the original API-only signal shape (some
 * callers/tests only care about the all-API case). Returns null for non-API runs.
 */
export function deriveRunApiSignal(
  tests: readonly TestResultForUpload[] | undefined,
): RunApiSignal | null {
  const sig = deriveRunTestType(tests);
  if (sig.testType !== 'api') return null;
  return { testType: 'api', apiProtocol: sig.apiProtocol ?? 'rest', metadata: sig.metadata ?? { testType: 'api', primaryProtocol: sig.apiProtocol ?? 'rest' } };
}

export function buildUploadPayload(
  report: TestRunReport,
  repoGitId: string,
  git: GitMetadata | null,
  ci: CIMetadata | null,
  tests?: readonly TestResultForUpload[],
  cloudFarm?: {
    cloudPlatform?: 'browserstack' | 'lambdatest';
    cloudBuildId?: string;
    cloudSessionId?: string;
  },
): RunUploadPayload {
  const environment = cloudFarm?.cloudPlatform
    ? cloudFarm.cloudPlatform
    : ci?.provider && ci.provider !== 'unknown' ? ci.provider : 'local';
  const runType = resolveSdkRunTypeFromEnv();
  // Derive the run-level test type (Web / API / Mobile / Unit). When the run is
  // all-API, also stamp each test's apiProtocol so the API report can show
  // per-test protocol badges; for non-API runs nothing is mutated on the tests.
  // NOTE: a MIXED run deliberately does NOT stamp apiProtocol on its api tests —
  // a leaked per-test protocol would trip the server's "any protocol => api"
  // run rule and misroute the whole run. The cloud tree instead defaults the
  // badge from the per-test testType. (See the api-classification regression test.)
  const typeSignal = deriveRunTestType(tests);
  const apiSignal = typeSignal.testType === 'api' ? typeSignal : null;
  const effectiveTests = apiSignal && tests
    ? tests.map((t) => (t.apiProtocol ? t : { ...t, apiProtocol: apiSignal.apiProtocol! }))
    : tests;
  const payload: RunUploadPayload = {
    runId: report.testRunId,
    repoGitId,
    startedAt: report.startedAt,
    summary: report.summary,
    timeline: report.timeline,
    environment,
    ...(runType ? { runType } : {}),
    ...(apiSignal
      ? { testType: apiSignal.testType, apiProtocol: apiSignal.apiProtocol, metadata: apiSignal.metadata }
      : { testType: typeSignal.testType }),
    ...(effectiveTests && effectiveTests.length > 0 ? { tests: effectiveTests } : {}),
    ...(git?.branch ? { branch: git.branch } : {}),
    ...(git?.commitSha ? { commit: git.commitSha } : {}),
    ...(git?.commitMessage ? { commitMessage: git.commitMessage } : {}),
    ...(git?.commitAuthor ? { commitAuthor: git.commitAuthor } : {}),
    ...(report.completedAt ? { finishedAt: report.completedAt } : {}),
    ...(report.totalDuration ? { duration: report.totalDuration } : {}),
    ...(ci?.provider ? { ciProvider: ci.provider } : {}),
    ...(ci?.runUrl ? { ciRunUrl: ci.runUrl } : {}),
    ...(cloudFarm?.cloudPlatform ? { cloudPlatform: cloudFarm.cloudPlatform } : {}),
    ...(cloudFarm?.cloudBuildId ? { cloudBuildId: cloudFarm.cloudBuildId } : {}),
    ...(cloudFarm?.cloudSessionId ? { cloudSessionId: cloudFarm.cloudSessionId } : {}),
  };

  // The 413 size-guard / strip lives in prepareBody (the single serialization
  // point), so the payload is measured once and stripped only if it actually
  // exceeds the limit AFTER serialization — and the offline-queue replay path
  // gets the identical treatment for free.
  return payload;
}

// ---------------------------------------------------------------------------
// Canonical `apiReport` shape mappers
//
// The cloud API-workspace inspector reads `apiReport.apiCalls` (ApiCall shape:
// statusCode/statusText/ok/durationMs/bytesIn/bytesOut) and
// `apiReport.protocolAssertions` (kind/description/status/detail). The capture
// layer records ApiCallRecord (responseStatusCode/responseTimeMs) and
// ApiAssertion (type/expected/actual/expression), so every cloud payload must
// map to the canonical shape — otherwise the panel renders empty cells (or, for
// assertions, nothing at all). Coalescing makes them idempotent if a row is
// already canonical.
// ---------------------------------------------------------------------------

/** UTF-8 byte length of a string body (0 for null/non-string). */
function bodyBytes(v: unknown): number {
  return typeof v === 'string' ? Buffer.byteLength(v, 'utf-8') : 0;
}

/** Map captured API calls to the cloud `apiReport.apiCalls` (ApiCall) shape. */
export function toApiReportCalls(
  records: readonly Record<string, unknown>[] | null | undefined,
): Record<string, unknown>[] {
  if (!Array.isArray(records)) return [];
  return records.map((c) => {
    const statusCode = (c.statusCode ?? c.responseStatusCode ?? null) as number | null;
    return {
      // Stable per-call id so protocolAssertions can be attributed to their call
      // (the assertion carries the matching callId). Preserved end-to-end even
      // though today's single-call inspector doesn't yet key on it.
      ...(c.id != null ? { id: c.id } : {}),
      ...(c.protocol != null ? { protocol: c.protocol } : {}),
      method: (c.method ?? null) as string | null,
      url: (c.url ?? null) as string | null,
      statusCode,
      statusText: (c.statusText ?? c.responseStatusText ?? null) as string | null,
      ok: typeof statusCode === 'number' ? statusCode < 400 : ((c.ok ?? null) as boolean | null),
      durationMs: (c.durationMs ?? c.responseTimeMs ?? null) as number | null,
      bytesIn: typeof c.bytesIn === 'number' ? c.bytesIn : bodyBytes(c.responseBody),
      bytesOut: typeof c.bytesOut === 'number' ? c.bytesOut : bodyBytes(c.requestBody),
      requestHeaders: (c.requestHeaders ?? null) as Record<string, string> | null,
      responseHeaders: (c.responseHeaders ?? null) as Record<string, string> | null,
      requestBody: (c.requestBody ?? null) as string | null,
      responseBody: (c.responseBody ?? null) as string | null,
      requestCookies: (c.requestCookies ?? null) as Record<string, unknown>[] | null,
      responseCookies: (c.responseCookies ?? null) as Record<string, unknown>[] | null,
    };
  });
}

/** Map captured API calls to the generic Network-inspector (network.json) shape. */
export function toNetworkRequestsFromApiCalls(
  records: readonly Record<string, unknown>[] | null | undefined,
): Record<string, unknown>[] {
  if (!Array.isArray(records)) return [];
  return records.map((c) => {
    const status = (c.responseStatusCode ?? c.statusCode ?? 0) as number;
    const ts = (c.timestamp ?? null) as string | null;
    return {
      method: c.method ?? 'GET',
      url: c.url ?? '',
      status,
      statusCode: status,
      type: 'xhr',
      size: bodyBytes(c.responseBody),
      duration: (c.responseTimeMs ?? c.durationMs ?? 0) as number,
      responseTimeMs: (c.responseTimeMs ?? c.durationMs ?? 0) as number,
      requestHeaders: c.requestHeaders ?? null,
      responseHeaders: c.responseHeaders ?? null,
      requestBody: c.requestBody ?? null,
      responseBody: c.responseBody ?? null,
      timestamp: ts,
      startedAt: ts,
    };
  });
}

/** Map captured assertions to the cloud `apiReport.protocolAssertions` shape. */
export function toProtocolAssertions(
  assertions: readonly Record<string, unknown>[] | null | undefined,
): Record<string, unknown>[] {
  if (!Array.isArray(assertions)) return [];
  const fmt = (v: unknown): string => {
    if (v === null || v === undefined) return String(v);
    if (typeof v === 'string') return v;
    try { return JSON.stringify(v); } catch { return String(v); }
  };
  return assertions.map((a) => {
    const status = a.status === 'failed' ? 'failed' : 'passed';
    const expr = typeof a.expression === 'string' && a.expression.trim() ? a.expression.trim() : null;
    const kind = (a.kind ?? a.type ?? 'assertion') as string;
    const detail =
      status === 'failed'
        ? `expected ${fmt(a.expected)}, received ${fmt(a.actual)}`
        : a.expected !== undefined
        ? `expected ${fmt(a.expected)}`
        : null;
    return {
      // callId links the assertion to its ApiCallRecord (preserved so a future
      // multi-call inspector can attribute assertions per request; harmless for
      // the current single-call view, which shows all of a test's assertions).
      ...(a.callId != null ? { callId: a.callId } : {}),
      kind,
      description: expr ?? (a.description as string | undefined) ?? `${kind} assertion`,
      status,
      detail,
    };
  });
}

// ---------------------------------------------------------------------------
// Retry Logic
// ---------------------------------------------------------------------------

async function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Strip null/undefined values from an object before sending to the cloud API. */
function stripNulls(obj: Record<string, unknown>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(obj)) {
    if (val !== null && val !== undefined) result[key] = val;
  }
  return result;
}

/**
 * Retry a fetch request with exponential backoff.
 * Handles 429 rate limits (reads Retry-After header), 5xx errors, and network failures.
 * Returns the response on success, or null after all retries exhausted.
 */
async function retryWithBackoff(
  url: string,
  init: RequestInit,
  onTokenRefresh?: () => Promise<string | null>,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT_MS,
  budget?: Budget,
): Promise<Response | null> {
  for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
    // ⛔ Refuse rather than start: a request begun with no budget left still
    // pays its own timeout, which is how one stalled upload became ten.
    if (budget?.expired()) return null;
    const slice = budget ? budget.slice(timeoutMs) : timeoutMs;
    if (slice <= 0) return null;
    try {
      const response = await withTimeout(url, init, slice);

      if (response.ok) return response;

      // 401 — attempt token refresh and retry once
      if (response.status === 401 && onTokenRefresh && attempt === 0) {
        const newToken = await onTokenRefresh();
        if (newToken) {
          const refreshedInit = {
            ...init,
            headers: { ...init.headers as Record<string, string>, Authorization: `Bearer ${newToken}` },
          };
          const retryResponse = await withTimeout(url, refreshedInit, slice);
          if (retryResponse.ok) return retryResponse;
        }
        return null; // Refresh failed or retry failed
      }

      // 429 — rate limited, respect Retry-After header
      if (response.status === 429 && attempt < MAX_RETRIES - 1) {
        const retryAfter = response.headers.get('Retry-After');
        const waitMs = retryAfter ? parseInt(retryAfter, 10) * 1000 : RETRY_DELAYS_MS[attempt];
        if (!isNaN(waitMs) && waitMs > 0) {
          await sleep(waitMs);
        } else {
          await sleep(RETRY_DELAYS_MS[attempt]);
        }
        continue;
      }

      // 5xx — retry with backoff
      if (response.status >= 500 && attempt < MAX_RETRIES - 1) {
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }

      // Return the response for caller-level handling (e.g., 409)
      return response;
    } catch {
      // Network error — retry with backoff
      if (attempt < MAX_RETRIES - 1) {
        await sleep(RETRY_DELAYS_MS[attempt]);
        continue;
      }
      return null;
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Compression
// ---------------------------------------------------------------------------

const gzipAsync = promisify(gzip);

/** A batch upload's per-test rows carry the heavy artifact arrays. */
type TestsCarrier = { tests?: Array<Record<string, unknown>> } & Record<string, unknown>;

/**
 * Shed the heaviest per-test arrays so an oversized batch fits under the server
 * limit instead of being lost whole to a 413. `dropApiCalls=false` keeps apiCalls
 * (the API-workspace inspector needs them); `true` drops them too as a last resort.
 */
function stripOversizeTests(payload: TestsCarrier, dropApiCalls: boolean): TestsCarrier {
  if (!Array.isArray(payload.tests)) return payload;
  const tests = payload.tests.map((t) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { consoleLogs: _c, networkRequests: _n, apiCalls, ...rest } = t;
    return dropApiCalls ? rest : { ...rest, ...(apiCalls !== undefined ? { apiCalls } : {}) };
  });
  return { ...payload, tests };
}

/** Every heavy per-test array carried in the batch payload (besides the failure blob). */
const HEAVY_TEST_KEYS = ['consoleLogs', 'networkRequests', 'apiCalls', 'actions', 'navigations', 'protocolAssertions'];

/**
 * Tier-3 strip: keep only lightweight per-test metadata — drop EVERY heavy array and
 * trim the failure to its message/line — so the per-test results (status, timing,
 * identity) still land even when console/network/apiCalls alone weren't enough.
 */
function stripToMinimalTests(payload: TestsCarrier): TestsCarrier {
  if (!Array.isArray(payload.tests)) return payload;
  const heavy = new Set(HEAVY_TEST_KEYS);
  const tests = payload.tests.map((t) => {
    const out: Record<string, unknown> = {};
    for (const k in t) {
      if (heavy.has(k)) continue;
      if (k === 'failure' && t[k] && typeof t[k] === 'object') {
        const f = t[k] as Record<string, unknown>;
        out.failure = { message: f.message ?? null, line: f.line ?? null };
      } else {
        out[k] = t[k];
      }
    }
    return out;
  });
  return { ...payload, tests };
}

/**
 * Tier-4 strip (absolute last resort): drop the per-test rows and any run-level heavy
 * arrays entirely, so the run record + summary still land instead of the WHOLE upload
 * being lost to a 413. Bounds the body to the run summary + metadata.
 */
function stripAllTests(payload: TestsCarrier): TestsCarrier {
  const heavyRunKeys = new Set(['tests', 'timeline', 'unifiedTimeline', 'navigations']);
  const out: Record<string, unknown> = {};
  for (const k in payload) if (!heavyRunKeys.has(k)) out[k] = (payload as Record<string, unknown>)[k];
  out.tests = [];
  return out as TestsCarrier;
}

/**
 * Serialize a batch payload ONCE, apply the 413 size-guard, and gzip large bodies
 * off the main thread. The server enforces its 100MB limit on the DECOMPRESSED body
 * and we gzip here, so the guard trips only for an extreme outlier; when it does it
 * sheds detail in tiers, RE-MEASURING after each, until the body fits — so a run is
 * NEVER lost whole to a 413 (the worst case still uploads the run summary). The tiers:
 *   1. drop consoleLogs/networkRequests (keep apiCalls for the inspector)
 *   2. also drop apiCalls
 *   3. shed all remaining per-test arrays (actions/navigations/assertions) + failure detail
 *   4. drop the per-test rows entirely — run summary only (absolute last resort)
 * Each tier warns; tier 4 is an error. Exported so the offline-queue replay path
 * compresses identically AND bounds the body the same way (otherwise a queued oversize
 * payload re-413s on every flush forever).
 */
export async function prepareBody(
  payload: unknown,
  maxBytes: number = MAX_BATCH_PAYLOAD_BYTES,
): Promise<{ body: Buffer | string; headers: Record<string, string> }> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  let json = JSON.stringify(payload);
  const carrier = payload as TestsCarrier;
  const testCount = Array.isArray(carrier?.tests) ? carrier.tests.length : 0;
  const over = (s: string): boolean => Buffer.byteLength(s, 'utf-8') > maxBytes;

  if (testCount > 0 && over(json)) {
    const limitMb = Math.max(1, Math.round(maxBytes / 1_048_576));

    // Tier 1 — drop consoleLogs/networkRequests, keep apiCalls.
    process.stderr.write(
      `[testrelic] WARNING: batch payload exceeds ${limitMb}MB — dropping consoleLogs/networkRequests ` +
      `from ${testCount} test(s) to avoid HTTP 413. Console/network detail will be missing in the cloud.\n`,
    );
    json = JSON.stringify(stripOversizeTests(carrier, false));

    // Tier 2 — also drop apiCalls.
    if (over(json)) {
      process.stderr.write(
        `[testrelic] WARNING: batch still exceeds ${limitMb}MB after dropping logs — also dropping apiCalls ` +
        `from ${testCount} test(s). API request/response detail will be missing in the cloud.\n`,
      );
      json = JSON.stringify(stripOversizeTests(carrier, true));
    }

    // Tier 3 — shed every remaining heavy per-test array + trim failure detail.
    if (over(json)) {
      process.stderr.write(
        `[testrelic] WARNING: batch still exceeds ${limitMb}MB — shedding remaining per-test detail ` +
        `(actions/navigations/assertions) from ${testCount} test(s). Only test results + summary will reach the cloud.\n`,
      );
      json = JSON.stringify(stripToMinimalTests(carrier));
    }

    // Tier 4 — last resort: drop per-test rows so the run summary still lands.
    if (over(json)) {
      process.stderr.write(
        `[testrelic] ERROR: batch still exceeds ${limitMb}MB after shedding all per-test detail — uploading ` +
        `run summary only (${testCount} per-test result(s) omitted) to avoid losing the run to HTTP 413.\n`,
      );
      json = JSON.stringify(stripAllTests(carrier));
    }
  }

  if (Buffer.byteLength(json, 'utf-8') > GZIP_THRESHOLD_BYTES) {
    headers['Content-Encoding'] = 'gzip';
    return { body: await gzipAsync(Buffer.from(json, 'utf-8')), headers };
  }
  return { body: json, headers };
}

// ---------------------------------------------------------------------------
// Batch Upload
// ---------------------------------------------------------------------------

/**
 * Upload a complete test run to the cloud.
 * POST /runs with the full payload.
 * Returns a structured result (never throws).
 */
export async function uploadBatchRun(
  endpoint: string,
  accessToken: string,
  payload: RunUploadPayload,
  onTokenRefresh?: () => Promise<string | null>,
  timeoutMs?: number,
  budget?: Budget,
): Promise<UploadResult | UploadFailure> {
  const url = `${endpoint}/runs`;
  const { body, headers } = await prepareBody(payload);
  headers['Authorization'] = `Bearer ${accessToken}`;

  const response = await retryWithBackoff(
    url,
    { method: 'POST', headers, body },
    onTokenRefresh,
    timeoutMs,
    budget,
  );

  if (response?.ok) {
    return { success: true, statusCode: response.status, error: null };
  }

  // 409 — duplicate run, treat as success (data already ingested)
  if (response?.status === 409) {
    return { success: true, statusCode: 409, error: null };
  }

  return {
    success: false,
    reason: response ? `Upload failed with status ${response.status}` : 'Upload failed after retries',
    statusCode: response?.status ?? null,
    payload: payload as unknown as Record<string, unknown>,
    targetEndpoint: url,
    method: 'POST',
  };
}

// ---------------------------------------------------------------------------
// Realtime Upload Functions
// ---------------------------------------------------------------------------

export interface RealtimeInitResult {
  readonly runId: string;
}

/**
 * Initialize a realtime run on the cloud.
 * POST /runs/init
 */
export async function initRealtimeRun(
  endpoint: string,
  accessToken: string,
  payload: {
    runId: string;
    repoGitId: string;
    branch: string | null;
    commit: string | null;
    commitMessage?: string | null;
    commitAuthor?: string | null;
    startedAt: string;
    totalTests: number | null;
    ciProvider: string | null;
    ciRunUrl: string | null;
    environment?: string | null;
    cloudPlatform?: string | null;
    cloudBuildId?: string | null;
    cloudSessionId?: string | null;
    runType?: SdkRunType;
    /** Run-level test-type default (e.g. 'e2e'); finalize overwrites with the derived slug. */
    testType?: string;
  },
  timeoutMs?: number,
): Promise<RealtimeInitResult | null> {
  const url = `${endpoint}/runs/init`;
  try {
    const response = await withTimeout(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${accessToken}`,
        },
        body: JSON.stringify(stripNulls(payload as unknown as Record<string, unknown>)),
      },
      timeoutMs ?? DEFAULT_REQUEST_TIMEOUT_MS,
    );
    if (!response.ok) return null;
    const responseBody = await response.json() as Record<string, unknown>;
    return { runId: responseBody.runId as string };
  } catch {
    return null;
  }
}

/**
 * Upload a single test result to the cloud (fire-and-forget).
 * POST /runs/{runId}/tests
 */
export function uploadTestResult(
  endpoint: string,
  accessToken: string,
  cloudRunId: string,
  testData: Record<string, unknown>,
): void {
  const url = `${endpoint}/runs/${cloudRunId}/tests`;
  // Fire-and-forget — intentionally not awaited
  void fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(testData),
  }).catch(() => {
    // Silently ignore realtime upload errors
  });
}

/**
 * Finalize a realtime run on the cloud with retry and backoff.
 * POST /runs/{runId}/finalize
 */
export async function finalizeRun(
  endpoint: string,
  accessToken: string,
  cloudRunId: string,
  payload: {
    finishedAt: string;
    duration: number;
    summary: Summary;
    commitMessage?: string | null;
    /** Run-level API signal — realtime init ran before any test, so the server learns the run is API here. */
    testType?: string;
    apiProtocol?: string;
    metadata?: Record<string, unknown>;
  },
): Promise<boolean> {
  const url = `${endpoint}/runs/${cloudRunId}/finalize`;
  const response = await retryWithBackoff(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  });
  return response?.ok ?? false;
}

/**
 * Fire-and-forget finalize for use in signal handlers (SIGINT/SIGTERM).
 * Does not await the response — the process may exit before it completes,
 * but the HTTP request will still be dispatched to the OS network stack.
 */
export function finalizeRunFireAndForget(
  endpoint: string,
  accessToken: string,
  cloudRunId: string,
  startedAt: string,
): void {
  const url = `${endpoint}/runs/${cloudRunId}/finalize`;
  const now = new Date();
  const payload = {
    finishedAt: now.toISOString(),
    duration: now.getTime() - new Date(startedAt).getTime(),
    summary: {},
  };
  void fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  }).catch(() => {
    // Best-effort — silently ignore errors in signal handlers
  });
}
